/**
 * api — the one door for backend HTTP calls.
 *
 * Components and stores ask for WHAT they want (`api.get('/api/regions')`,
 * `api.post('/api/search_v2/search', body)`); this module owns HOW the call
 * is made:
 *
 *   - base URL      — resolved through apiBase (split-deploy aware), so
 *                     callers write '/api/...' paths and never touch
 *                     window.location.origin
 *   - identity      — credentials: 'include' (HttpOnly session cookie) +
 *                     X-Session-Id + in-memory Bearer token on every call,
 *                     instead of each call site re-deciding (or forgetting)
 *   - timeout       — hard cap per attempt (fetchWithTimeout semantics)
 *   - cold-start    — 502 retry with backoff for Render spin-ups, opt-in
 *                     per call via `retry: 'cold-start'`, with an
 *                     onWarmingUp hook for "backend warming" UI
 *   - cancellation  — every call is abortable; `cancelKey` gives
 *                     latest-wins semantics (a new call with the same key
 *                     aborts the in-flight one, INCLUDING mid-backoff),
 *                     which is what ends the "cold backend locks the UI
 *                     for 5 minutes" failure mode
 *
 * Errors: non-2xx → ApiError (status, body text). Abort → AbortError
 * (callers should treat it as "superseded", not a failure).
 *
 * `tests/apiClientGuard.test.ts` is the ratchet: no NEW module may call
 * fetch/fetchWithTimeout directly.
 */
import { API_BASE } from '../utils/apiBase';
import { safeSession } from '../utils/safeStorage';

export class ApiError extends Error {
  status: number;
  bodyText: string;
  constructor(status: number, bodyText: string, url: string) {
    super(`API ${status} on ${url}`);
    this.name = 'ApiError';
    this.status = status;
    this.bodyText = bodyText;
  }
}

export interface ApiOptions {
  /** Per-attempt hard timeout. Default 30s. */
  timeoutMs?: number;
  /** '502 cold-start backoff' | no retry. Default 'none'. */
  retry?: 'cold-start' | 'none';
  /** Backoff schedule for cold-start retries. */
  retryDelaysMs?: number[];
  /** Called once when the first 502 is seen (drive "warming up" UI). */
  onWarmingUp?: () => void;
  /** Latest-wins: starting a call with this key aborts any in-flight call
   *  with the same key (including one sleeping between retries). */
  cancelKey?: string;
  /** External abort (e.g. component unmount). */
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /** Attach the in-memory Bearer token (cookie always rides regardless).
   *  Default true. */
  auth?: boolean;
}

// Default cold-start schedule (~61s window) — Render spins the backend down
// after inactivity and the LLM-heavy endpoints come up after /health, so a
// fully cold boot can take 40-60s. Better a long "warming up" than a blank
// screen; a superseding call aborts the wait instantly via cancelKey.
export const COLD_START_DELAYS_MS = [5000, 8000, 12000, 16000, 20000];

const DEFAULT_TIMEOUT_MS = 30_000;

// In-flight latest-wins registry.
const inflight = new Map<string, AbortController>();

/** Abort the in-flight call registered under `key` (no-op when none). */
export function cancel(key: string): void {
  inflight.get(key)?.abort();
  inflight.delete(key);
}

export function resolveUrl(path: string): string {
  if (/^https?:\/\//.test(path)) return path;
  return `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
}

function sessionId(): string {
  let stored: string | null = null;
  try { stored = safeSession.get('ts_session_id'); } catch { /* private mode */ }
  return (window as { __ts_session_id?: string }).__ts_session_id || stored || '';
}

function bearerToken(): string | null {
  try {
    // Lazy require avoids a static cycle api → authStore → api. CRA's
    // eslint config doesn't define @typescript-eslint/no-var-requires, so
    // disabling that rule by name is itself the build error — the rule
    // isn't on, and nothing here needs suppressing.
    const { useAuthStore } = require('../stores/authStore');
    return useAuthStore.getState().token || null;
  } catch {
    return null;
  }
}

function abortableSleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) return reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
    const t = setTimeout(() => { cleanup(); resolve(); }, ms);
    const onAbort = () => { cleanup(); reject(signal.reason ?? new DOMException('Aborted', 'AbortError')); };
    const cleanup = () => { clearTimeout(t); signal.removeEventListener('abort', onAbort); };
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

/** Low-level: returns the raw Response (2xx or not). Prefer get/post. */
export async function request(
  path: string,
  init: RequestInit = {},
  opts: ApiOptions = {},
): Promise<Response> {
  const url = resolveUrl(path);
  const {
    timeoutMs = DEFAULT_TIMEOUT_MS,
    retry = 'none',
    retryDelaysMs = COLD_START_DELAYS_MS,
    onWarmingUp,
    cancelKey,
    signal: callerSignal,
    headers: extraHeaders,
    auth = true,
  } = opts;

  // One controller governs the whole call (all attempts + backoff sleeps).
  const controller = new AbortController();
  if (callerSignal) {
    if (callerSignal.aborted) controller.abort(callerSignal.reason);
    else callerSignal.addEventListener('abort', () => controller.abort(callerSignal.reason), { once: true });
  }
  if (cancelKey) {
    inflight.get(cancelKey)?.abort();
    inflight.set(cancelKey, controller);
  }

  const sid = sessionId();
  const token = auth ? bearerToken() : null;
  const headers: Record<string, string> = {
    ...(init.body != null ? { 'Content-Type': 'application/json' } : {}),
    ...(sid ? { 'X-Session-Id': sid } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(init.headers as Record<string, string> | undefined),
    ...extraHeaders,
  };

  const attempt = async (): Promise<Response> => {
    // Per-attempt timeout that still honors the master controller.
    const attemptController = new AbortController();
    const onMasterAbort = () => attemptController.abort(controller.signal.reason);
    if (controller.signal.aborted) attemptController.abort(controller.signal.reason);
    else controller.signal.addEventListener('abort', onMasterAbort, { once: true });
    const timer = setTimeout(() => attemptController.abort(new DOMException('Timeout', 'AbortError')), timeoutMs);
    try {
      return await fetch(url, {
        ...init,
        headers,
        credentials: init.credentials ?? 'include',
        signal: attemptController.signal,
      });
    } finally {
      clearTimeout(timer);
      controller.signal.removeEventListener('abort', onMasterAbort);
    }
  };

  try {
    let response = await attempt();
    if (retry === 'cold-start' && response.status === 502) {
      onWarmingUp?.();
      for (const delay of retryDelaysMs) {
        await abortableSleep(delay, controller.signal);
        response = await attempt();
        if (response.status !== 502) break;
      }
    }
    return response;
  } finally {
    if (cancelKey && inflight.get(cancelKey) === controller) {
      inflight.delete(cancelKey);
    }
  }
}

async function requestJson<T>(path: string, init: RequestInit, opts: ApiOptions): Promise<T> {
  const response = await request(path, init, opts);
  if (!response.ok) {
    let bodyText = '';
    try { bodyText = await response.text(); } catch { /* stream gone */ }
    throw new ApiError(response.status, bodyText.slice(0, 500), resolveUrl(path));
  }
  return response.json() as Promise<T>;
}

export function get<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
  return requestJson<T>(path, { method: 'GET' }, opts);
}

export function post<T = unknown>(path: string, body?: unknown, opts: ApiOptions = {}): Promise<T> {
  return requestJson<T>(
    path,
    { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) },
    opts,
  );
}

/** True when an error means "this call was superseded/cancelled" — callers
 *  should return silently instead of surfacing a failure state. */
export function isAbortError(err: unknown): boolean {
  return err instanceof DOMException
    ? err.name === 'AbortError'
    : (err as { name?: string } | null)?.name === 'AbortError';
}

export const api = { get, post, request, cancel, isAbortError };
export default api;
